/** The image viewport: WebGL image layer + 2-D overlay + all pointer interaction. */
import { signal, useSignalEffect } from '@preact/signals';
import { useEffect, useRef, useState } from 'preact/hooks';
import { colormapLut } from '../core/colormaps';
import { quantityInfo } from '../core/pipeline';
import type { EllipseRoi, LineRoi, PointRoi, PolygonRoi, RectRoi, Roi } from '../core/roi';
import { fmt } from '../core/units';
import { addRoi, nextRoiDefaults, updateRoi } from '../app/actions';
import {
  compareMode,
  ctxA,
  ctxB,
  display,
  dragging,
  frames,
  hover,
  imageSize,
  lineResults,
  measurement,
  plotHover,
  quantity,
  rois,
  selectedId,
  selectedLine,
  snapshotRois,
  spatial,
  tool,
  windowRange,
} from '../app/store';
import { liveRate } from '../app/capture';
import { buildColorRGBA } from './colorImage';
import { dragHandle, hitTest, snapToCenter } from './handles';
import { drawOverlay, type LineMarks } from './overlay';
import { ImageRenderer } from './renderer';
import { fitView, toImage, zoomAt, type View } from './transform';
import { calibrationRequest, contourData, liveVideo, polygonDraft, snapPixels, viewportApi, viewSig } from './viewState';

type Drag =
  | { kind: 'pan'; sx: number; sy: number; view0: View }
  | { kind: 'edit'; roi: Roi; handle: string; ix: number; iy: number; recorded: boolean }
  | { kind: 'create'; draft: Roi; ix: number; iy: number; calibrate: boolean };

export const spaceDown = { current: false };
/** Incremented when the WebGL context is recreated, to re-run texture uploads. */
const glEpoch = signal(0);

export function Viewport() {
  const wrap = useRef<HTMLDivElement>(null);
  const glCanvas = useRef<HTMLCanvasElement>(null);
  const ovCanvas = useRef<HTMLCanvasElement>(null);
  const renderer = useRef<ImageRenderer | null>(null);
  const size = useRef({ w: 1, h: 1 });
  const drag = useRef<Drag | null>(null);
  const draft = useRef<Roi | null>(null);
  const frameReq = useRef(0);
  const uploaded = useRef<{ color: unknown; plane: unknown; clip: unknown; lut: string }>({
    color: null,
    plane: null,
    clip: null,
    lut: '',
  });
  const lastSize = useRef<string>('');
  const [glError, setGlError] = useState<string | null>(null);
  const [cursor, setCursor] = useState('default');

  const schedule = () => {
    if (frameReq.current) return;
    frameReq.current = requestAnimationFrame(() => {
      frameReq.current = 0;
      paint();
    });
  };

  const paint = () => {
    const r = renderer.current;
    const d = display.value;
    const [lo, hi] = windowRange.value;
    const { w, h } = size.current;
    const dpr = window.devicePixelRatio || 1;
    const live = liveVideo.value;
    if (r) {
      r.render(viewSig.value, {
        mode: d.mode === 'color' ? 0 : d.mode === 'gray' ? 1 : 2,
        lo,
        hi,
        log: d.log,
        showClip: d.showClip,
        smooth: true,
        cssW: w,
        cssH: h,
        dpr,
      });
    }
    const oc = ovCanvas.current;
    if (!oc) return;
    const W = Math.round(w * dpr);
    const H = Math.round(h * dpr);
    if (oc.width !== W || oc.height !== H) {
      oc.width = W;
      oc.height = H;
    }
    const c = oc.getContext('2d')!;
    const img = imageSize.value;
    const m = measurement.value;
    const ph = plotHover.value;
    let plotMarker = null;
    if (ph) {
      const l = rois.value.find((x) => x.id === ph.roiId);
      if (l && l.kind === 'line') {
        const L = Math.hypot(l.x1 - l.x0, l.y1 - l.y0) || 1;
        plotMarker = { x: l.x0 + ((l.x1 - l.x0) / L) * ph.t, y: l.y0 + ((l.y1 - l.y0) / L) * ph.t };
      }
    }
    let lineMarks: LineMarks | null = null;
    const sl = selectedLine.value;
    const res = sl ? lineResults.value.get(sl.id) : null;
    if (sl && res) {
      const ticks: LineMarks['ticks'] = [];
      for (const p of res.metrics.peaks) {
        ticks.push({ t: p.x, kind: 'peak' });
        const fw = p.widths.find((w) => Math.abs(w.level - 0.5) < 1e-9) ?? p.widths[0];
        if (fw && fw.left !== null && fw.right !== null) ticks.push({ t: fw.left, kind: 'edge' }, { t: fw.right, kind: 'edge' });
      }
      lineMarks = { roiId: sl.id, ticks };
    }
    const cd = contourData.value;
    drawOverlay(c, {
      view: viewSig.value,
      cssW: w,
      cssH: h,
      dpr,
      imageW: live ? live.videoWidth : (img?.w ?? 0),
      imageH: live ? live.videoHeight : (img?.h ?? 0),
      rois: rois.value,
      selectedId: selectedId.value,
      draft: draft.current,
      polygonDraft: polygonDraft.value,
      plane: m?.plane ?? null,
      showGrid: d.showGrid,
      showValues: d.showValues,
      showLabels: d.showLabels,
      showScaleBar: d.showScaleBar,
      spatial: spatial.value,
      contours: cd ? { levels: cd, color: d.contours.color, labels: d.contours.labels } : null,
      plotMarker,
      lineMarks,
      legend:
        d.showLegend && d.mode !== 'color' && m
          ? { mode: d.mode, colormap: d.colormap, bands: d.bands, lo, hi, log: d.log, label: m.axis }
          : null,
      live: !!live,
    });
  };

  // ---------------------------------------------------------------- setup
  useEffect(() => {
    try {
      renderer.current = new ImageRenderer(glCanvas.current!);
    } catch (e) {
      setGlError((e as Error).message);
    }
    // GPU resets (driver update, sleep/wake, memory pressure) lose all textures: recreate and re-upload.
    const gl = glCanvas.current!;
    const onLost = (e: Event) => {
      e.preventDefault();
      renderer.current = null;
    };
    const onRestored = () => {
      try {
        renderer.current = new ImageRenderer(gl);
        uploaded.current = { color: null, plane: null, clip: null, lut: '' };
        glEpoch.value++;
      } catch (err) {
        setGlError((err as Error).message);
      }
    };
    gl.addEventListener('webglcontextlost', onLost);
    gl.addEventListener('webglcontextrestored', onRestored);
    const ro = new ResizeObserver(() => {
      const el = wrap.current!;
      const prev = size.current;
      size.current = { w: el.clientWidth, h: el.clientHeight };
      // Keep the image centre fixed on resize.
      const v = viewSig.value;
      viewSig.value = { ...v, tx: v.tx + (size.current.w - prev.w) / 2, ty: v.ty + (size.current.h - prev.h) / 2 };
      if (prev.w <= 1) fit();
      schedule();
    });
    ro.observe(wrap.current!);
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = ovCanvas.current!.getBoundingClientRect();
      const dy = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY;
      const factor = Math.exp(-dy * (e.ctrlKey ? 0.01 : 0.0015));
      viewSig.value = zoomAt(viewSig.value, e.clientX - rect.left, e.clientY - rect.top, factor);
    };
    const oc = ovCanvas.current!;
    oc.addEventListener('wheel', onWheel, { passive: false });
    viewportApi.current = {
      fit,
      oneToOne: () => {
        const { w, h } = size.current;
        const dpr = window.devicePixelRatio || 1;
        viewSig.value = zoomAt(viewSig.value, w / 2, h / 2, 1 / dpr / viewSig.value.scale);
      },
      zoomBy: (f) => (viewSig.value = zoomAt(viewSig.value, size.current.w / 2, size.current.h / 2, f)),
      centerOn: (x, y) => {
        const v = viewSig.value;
        viewSig.value = { ...v, tx: size.current.w / 2 - x * v.scale, ty: size.current.h / 2 - y * v.scale };
      },
      snapshotView: async () => {
        paint();
        const gl = glCanvas.current!;
        const out = document.createElement('canvas');
        out.width = gl.width;
        out.height = gl.height;
        const c = out.getContext('2d')!;
        c.drawImage(gl, 0, 0);
        c.drawImage(ovCanvas.current!, 0, 0);
        return new Promise((res) => out.toBlob(res, 'image/png'));
      },
    };
    return () => {
      gl.removeEventListener('webglcontextlost', onLost);
      gl.removeEventListener('webglcontextrestored', onRestored);
      ro.disconnect();
      oc.removeEventListener('wheel', onWheel);
      renderer.current?.dispose();
      viewportApi.current = null;
    };
  }, []);

  const fit = () => {
    const live = liveVideo.value;
    const img = live ? { w: live.videoWidth, h: live.videoHeight } : imageSize.value;
    if (!img || !img.w) return;
    viewSig.value = fitView(img.w, img.h, size.current.w, size.current.h);
  };

  // ---------------------------------------------------------------- GPU uploads
  useSignalEffect(() => {
    void glEpoch.value;
    const r = renderer.current;
    const d = display.value;
    const mode = compareMode.value;
    const ctx = mode === 'B' ? (ctxB.value ?? ctxA.value) : (ctxA.value ?? ctxB.value);
    if (!r) return;
    if (!ctx) {
      r.clearImage();
      uploaded.current = { color: null, plane: null, clip: null, lut: '' };
      schedule();
      return;
    }
    const key = `${ctx.frame.width}x${ctx.frame.height}`;
    if (key !== lastSize.current) {
      lastSize.current = key;
      requestAnimationFrame(fit);
    }
    if (d.mode === 'color' && liveVideo.value) {
      // Live colour view draws the video directly; live frames only feed the measurements.
    } else if (d.mode === 'color') {
      const tag = { ctx, ev: d.exposure };
      const prev = uploaded.current.color as typeof tag | null;
      if (!prev || prev.ctx !== ctx || prev.ev !== d.exposure) {
        r.setColor(buildColorRGBA(ctx, d.exposure), ctx.frame.width, ctx.frame.height);
        uploaded.current.color = tag;
      }
    } else {
      const m = measurement.value;
      if (m && uploaded.current.plane !== m.plane) {
        r.setPlane(m.plane.data, m.plane.width, m.plane.height);
        uploaded.current.plane = m.plane;
      }
      const lk = `${d.mode}:${d.colormap}:${d.bands}`;
      if (uploaded.current.lut !== lk) {
        r.setLut(colormapLut(d.mode === 'gray' ? 'gray' : d.colormap, d.mode === 'gray' ? 0 : d.bands));
        uploaded.current.lut = lk;
      }
    }
    if (d.showClip) {
      const clip = measurement.value?.clip ?? null;
      if (uploaded.current.clip !== clip) {
        r.setClip(clip, ctx.frame.width, ctx.frame.height);
        uploaded.current.clip = clip;
      }
    }
    schedule();
  });

  // Repaint whenever anything drawn changes.
  useSignalEffect(() => {
    void viewSig.value;
    void rois.value;
    void selectedId.value;
    void display.value;
    void windowRange.value;
    void plotHover.value;
    void polygonDraft.value;
    void contourData.value;
    void spatial.value;
    void lineResults.value;
    void measurement.value;
    schedule();
  });

  // Live capture: repaint continuously.
  useSignalEffect(() => {
    const v = liveVideo.value;
    void glEpoch.value;
    const r = renderer.current;
    if (!r) return;
    // Quantity / false-colour views render the live frames through the pipeline instead of the raw video.
    r.setVideo(v && display.value.mode === 'color' ? v : null);
    if (!v) {
      schedule();
      return;
    }
    let raf = 0;
    let fitted = false;
    const loop = () => {
      if (!fitted && v.videoWidth) {
        fitted = true;
        fit();
      }
      paint();
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  });

  // ---------------------------------------------------------------- interaction
  const local = (e: PointerEvent | MouseEvent): [number, number] => {
    const r = ovCanvas.current!.getBoundingClientRect();
    return [e.clientX - r.left, e.clientY - r.top];
  };
  /** Snapping: lines and points go to pixel centres; area-ROI edges and polygon vertices to pixel boundaries. */
  const snapFn = (e: { ctrlKey: boolean; metaKey: boolean }, kind: 'center' | 'edge' = 'center') => {
    const on = snapPixels.value !== (e.ctrlKey || e.metaKey);
    if (!on) return (v: number) => v;
    return kind === 'edge' ? Math.round : snapToCenter;
  };
  const snapKindFor = (k: Roi['kind'] | string): 'center' | 'edge' =>
    k === 'rect' || k === 'ellipse' || k === 'polygon' ? 'edge' : 'center';

  const finishPolygon = () => {
    const d = polygonDraft.value;
    polygonDraft.value = null;
    if (!d || d.pts.length < 3) return;
    addRoi({ ...nextRoiDefaults('polygon'), kind: 'polygon', pts: d.pts } as PolygonRoi);
    tool.value = 'select';
  };

  const onPointerDown = (e: PointerEvent) => {
    const el = ovCanvas.current!;
    el.focus();
    const [sx, sy] = local(e);
    const [ix, iy] = toImage(viewSig.value, sx, sy);
    const t = tool.value;
    if (e.button === 1 || (e.button === 0 && (t === 'pan' || spaceDown.current)) || e.button === 2) {
      if (e.button === 2) return;
      drag.current = { kind: 'pan', sx, sy, view0: viewSig.value };
      el.setPointerCapture(e.pointerId);
      setCursor('grabbing');
      return;
    }
    if (e.button !== 0) return;
    const snap = snapFn(e, snapKindFor(t));
    if (t === 'select') {
      const hit = hitTest(rois.value, selectedId.value, sx, sy, viewSig.value);
      if (hit) {
        selectedId.value = hit.roi.id;
        if (!hit.roi.locked) {
          drag.current = { kind: 'edit', roi: hit.roi, handle: hit.handle, ix, iy, recorded: false };
          el.setPointerCapture(e.pointerId);
        }
        return;
      }
      selectedId.value = null;
      drag.current = { kind: 'pan', sx, sy, view0: viewSig.value };
      el.setPointerCapture(e.pointerId);
      return;
    }
    if (t === 'polygon') {
      const p = { x: snap(ix), y: snap(iy) };
      const d = polygonDraft.value;
      if (d && d.pts.length >= 3) {
        const f = d.pts[0];
        const [fx, fy] = [f.x * viewSig.value.scale + viewSig.value.tx, f.y * viewSig.value.scale + viewSig.value.ty];
        if (Math.hypot(sx - fx, sy - fy) < 9) {
          finishPolygon();
          return;
        }
      }
      polygonDraft.value = { pts: [...(d?.pts ?? []), p], cursor: p };
      return;
    }
    const x = snap(ix);
    const y = snap(iy);
    const base = (k: Roi['kind']) => nextRoiDefaults(k);
    let r: Roi;
    switch (t) {
      case 'line':
      case 'calibrate':
        r = { ...base('line'), kind: 'line', x0: x, y0: y, x1: x, y1: y, width: 1 } as LineRoi;
        if (t === 'calibrate') r = { ...r, name: 'Calibration', color: '#ffffff' };
        break;
      case 'rect':
        r = { ...base('rect'), kind: 'rect', x, y, w: 0, h: 0 } as RectRoi;
        break;
      case 'ellipse':
        r = { ...base('ellipse'), kind: 'ellipse', cx: x, cy: y, rx: 0, ry: 0 } as EllipseRoi;
        break;
      case 'point': {
        const p = { ...base('point'), kind: 'point', x: snapToCenter(ix), y: snapToCenter(iy), size: 1 } as PointRoi;
        addRoi(p);
        tool.value = 'select';
        return;
      }
      default:
        return;
    }
    draft.current = r;
    drag.current = { kind: 'create', draft: r, ix: x, iy: y, calibrate: t === 'calibrate' };
    el.setPointerCapture(e.pointerId);
    dragging.value = true;
  };

  const onPointerMove = (e: PointerEvent) => {
    const [sx, sy] = local(e);
    const [ix, iy] = toImage(viewSig.value, sx, sy);
    hover.value = { x: ix, y: iy };
    const d = drag.current;
    const snap = snapFn(e, snapKindFor(d?.kind === 'create' ? d.draft.kind : tool.value));
    if (!d) {
      const pd = polygonDraft.value;
      if (pd) polygonDraft.value = { ...pd, cursor: { x: snap(ix), y: snap(iy) } };
      const t = tool.value;
      if (spaceDown.current || t === 'pan') setCursor('grab');
      else if (t === 'select') setCursor(hitTest(rois.value, selectedId.value, sx, sy, viewSig.value)?.cursor ?? 'default');
      else setCursor('crosshair');
      return;
    }
    if (d.kind === 'pan') {
      viewSig.value = { ...d.view0, tx: d.view0.tx + sx - d.sx, ty: d.view0.ty + sy - d.sy };
      return;
    }
    if (d.kind === 'edit') {
      if (!d.recorded) {
        if (Math.hypot(ix - d.ix, iy - d.iy) * viewSig.value.scale < 2) return;
        snapshotRois();
        d.recorded = true;
        dragging.value = true;
      }
      const next = dragHandle(d.roi, d.handle, ix, iy, ix - d.ix, iy - d.iy, {
        shift: e.shiftKey,
        alt: e.altKey,
        snap: snapFn(e, snapKindFor(d.roi.kind)),
      });
      updateRoi(d.roi.id, next);
      return;
    }
    // create
    const x = snap(ix);
    const y = snap(iy);
    const o = d.draft;
    let r: Roi = o;
    if (o.kind === 'line') {
      r = dragHandle(o, 'p1', x, y, 0, 0, { shift: e.shiftKey, alt: false, snap });
    } else if (o.kind === 'rect' || o.kind === 'ellipse') {
      let x0 = d.ix;
      let y0 = d.iy;
      let w = x - x0;
      let h = y - y0;
      if (e.shiftKey) {
        const s = Math.max(Math.abs(w), Math.abs(h));
        w = s * Math.sign(w || 1);
        h = s * Math.sign(h || 1);
      }
      if (e.altKey) {
        x0 -= w;
        y0 -= h;
        w *= 2;
        h *= 2;
      }
      const nx = Math.min(x0, x0 + w);
      const ny = Math.min(y0, y0 + h);
      r =
        o.kind === 'rect'
          ? { ...o, x: nx, y: ny, w: Math.abs(w), h: Math.abs(h) }
          : { ...o, cx: nx + Math.abs(w) / 2, cy: ny + Math.abs(h) / 2, rx: Math.abs(w) / 2, ry: Math.abs(h) / 2 };
    }
    d.draft = r;
    draft.current = r;
    schedule();
  };

  const onPointerUp = (e: PointerEvent) => {
    const d = drag.current;
    drag.current = null;
    dragging.value = false;
    try {
      ovCanvas.current!.releasePointerCapture(e.pointerId);
    } catch {
      /* not captured */
    }
    if (!d) return;
    if (d.kind === 'pan') {
      setCursor(tool.value === 'pan' ? 'grab' : 'default');
      return;
    }
    if (d.kind === 'create') {
      draft.current = null;
      const r = d.draft;
      const scale = viewSig.value.scale;
      let ok = false;
      if (r.kind === 'line') ok = Math.hypot(r.x1 - r.x0, r.y1 - r.y0) * scale >= 3;
      else if (r.kind === 'rect') ok = r.w * scale >= 3 && r.h * scale >= 3;
      else if (r.kind === 'ellipse') ok = r.rx * scale >= 2 && r.ry * scale >= 2;
      if (ok) {
        if (d.calibrate && r.kind === 'line') calibrationRequest.value = { lengthPx: Math.hypot(r.x1 - r.x0, r.y1 - r.y0) };
        else addRoi(r);
      }
      tool.value = 'select';
      schedule();
    }
  };

  const onDblClick = (e: MouseEvent) => {
    if (tool.value === 'polygon') {
      // The second click of the double-click added a duplicate vertex.
      const d = polygonDraft.value;
      if (d) polygonDraft.value = { ...d, pts: d.pts.slice(0, -1) };
      finishPolygon();
      return;
    }
    if (tool.value === 'select') {
      const [sx, sy] = local(e);
      if (!hitTest(rois.value, selectedId.value, sx, sy, viewSig.value)) fit();
    }
  };

  const hasImage = !!(frames.A.value || frames.B.value || liveVideo.value);
  return (
    <div class="viewport" ref={wrap}>
      <canvas ref={glCanvas} class="viewport-gl" />
      <canvas
        ref={ovCanvas}
        class="viewport-overlay"
        tabIndex={0}
        style={{ cursor }}
        data-testid="viewport"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onPointerLeave={() => {
          if (!drag.current) hover.value = null;
        }}
        onDblClick={onDblClick}
        onContextMenu={(e) => e.preventDefault()}
      />
      {!hasImage && <EmptyState />}
      {glError && <div class="viewport-error">WebGL2 unavailable: {glError}</div>}
      <StatusBar />
    </div>
  );
}

function EmptyState() {
  return (
    <div class="empty-state">
      <div class="empty-title">Place something in the aperture</div>
      <ul>
        <li>
          <kbd>Drop</kbd> an image file here
        </li>
        <li>
          <kbd>⌘/Ctrl</kbd> + <kbd>V</kbd> to paste from the clipboard
        </li>
        <li>
          <kbd>Open…</kbd> or <kbd>Capture</kbd> in the toolbar
        </li>
      </ul>
      <div class="empty-sub">PNG · JPEG · WebP · AVIF · TIFF · PFM · PGM/PPM · HDR · EXR · FITS · camera RAW / DNG</div>
    </div>
  );
}

function StatusBar() {
  const v = viewSig.value;
  const h = hover.value;
  const img = imageSize.value;
  const m = measurement.value;
  const live = liveVideo.value;
  let pos = '';
  let val = '';
  if (h && img) {
    const px = Math.floor(h.x);
    const py = Math.floor(h.y);
    if (px >= 0 && py >= 0 && px < img.w && py < img.h) {
      pos = `x ${px}  y ${py}`;
      if (m) {
        const p = m.plane;
        const lx = px - p.ox;
        const ly = py - p.oy;
        if (lx >= 0 && ly >= 0 && lx < p.width && ly < p.height)
          val = `${quantityInfo(quantity.value).short} ${fmt(p.data[ly * p.width + lx])} ${m.unit}`;
      }
    }
  }
  const dpr = window.devicePixelRatio || 1;
  return (
    <div class="statusbar">
      <span data-help="status.zoom">{fmt(v.scale * dpr * 100, 4)} %</span>
      {img && (
        <span>
          {img.w} × {img.h}
        </span>
      )}
      <span class="mono">{pos}</span>
      <span class="mono strong">{val}</span>
      {compareMode.value !== 'A' && <span class="badge">view: {compareMode.value}</span>}
      {live && (
        <span class="badge live" data-help="capture.liveRate" data-testid="live-badge">
          ● LIVE{liveRate.value > 0 ? ` · measuring ${liveRate.value.toFixed(1)} Hz` : ''}
        </span>
      )}
      {snapPixels.value && <span class="badge">snap</span>}
    </div>
  );
}
